/* ==========================================================================
   MRPIECE — Mix & Match look builder.

   The offer this drives is a real Shopify automatic discount, not a display
   trick:

     MRPIECE Mix & Match — Any 2 Pieces 15% Off   (min qty 2, collection mix-match)
     MRPIECE Mix & Match — Any 3+ Pieces 20% Off  (min qty 3, collection mix-match)

   Both are PRODUCT-class, neither combines with the other or with the
   store-wide "Bundle Deal – 10% Off 2+ Items", and Shopify awards whichever is
   worth most to the customer. That is why the tiers below are read from the
   section's own data attributes, which the Liquid writes from the theme
   editor: change the discount in Shopify, change the setting, and this file
   does not need touching. It must never invent a rate.

   Every price is handled in cents. Nothing here parses a formatted string back
   into a number.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- money ------------------------------------------------------ */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';
    var num = (cents / 100.0).toFixed(precision);
    var parts = num.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = (format || '${{amount}}').match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  /* ---------- analytics --------------------------------------------------- */

  // Pushed to dataLayer, mirrored to gtag when present, and re-emitted as a DOM
  // event so any tag manager or pixel already on the store can subscribe
  // without this file knowing about it. Deliberately does NOT emit add_to_cart
  // or begin_checkout: Shopify's own Customer Events already fire those, and
  // duplicating them would double-count the funnel.
  function track(name, payload) {
    var data = payload || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, data); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('mrpiece:' + name, { detail: data })); } catch (e) {}
  }

  /* ---------- card helpers ------------------------------------------------ */

  function readCard(el) {
    var variants = [];
    try { variants = JSON.parse(el.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
    return {
      el: el,
      id: el.getAttribute('data-id'),
      handle: el.getAttribute('data-handle'),
      title: el.getAttribute('data-title') || '',
      brand: el.getAttribute('data-brand') || '',
      type: el.getAttribute('data-type') || '',
      url: el.getAttribute('data-url') || '',
      price: parseInt(el.getAttribute('data-price'), 10) || 0,
      image: el.getAttribute('data-image') || '',
      colors: (el.getAttribute('data-colors') || '').split(',').filter(Boolean),
      variants: variants
    };
  }

  function chosenVariant(el) {
    var card = readCard(el);
    if (card.variants.length === 1) return card.variants[0];
    var picked = el.querySelector('.mm-size[aria-pressed="true"]');
    if (!picked) return null;
    var id = picked.getAttribute('data-variant-id');
    for (var i = 0; i < card.variants.length; i++) {
      if (String(card.variants[i].id) === String(id)) return card.variants[i];
    }
    return null;
  }

  function renderSizes(el) {
    var host = el.querySelector('.mm-card__sizes');
    if (!host) return;
    var card = readCard(el);
    if (card.variants.length <= 1) { host.hidden = true; host.innerHTML = ''; return; }
    if (host.dataset.built === '1') { host.hidden = false; return; }
    var html = '';
    for (var i = 0; i < card.variants.length; i++) {
      var v = card.variants[i];
      html += '<button type="button" class="mm-size" data-variant-id="' + v.id + '" aria-pressed="false">' +
        String(v.t).replace(/[<>&]/g, '') + '</button>';
    }
    host.innerHTML = html;
    host.dataset.built = '1';
    host.hidden = false;
  }

  /* ---------- the builder ------------------------------------------------- */

  function initSection(root) {
    if (root.dataset.mmReady === '1') return;
    root.dataset.mmReady = '1';

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var tier2 = parseFloat(root.getAttribute('data-tier2')) || 15;
    var tier3 = parseFloat(root.getAttribute('data-tier3')) || 20;

    // Free pairing: any piece goes with any other piece. When this is on, the
    // per-category "goes with" lists are ignored entirely, so a customer can
    // put a cap with a bag with a pair of slides without the builder deciding
    // for them. The lists stay authored in the theme editor, so turning this
    // off restores curation without re-entering them.
    var freePairing = root.getAttribute('data-free-pairing') === '1';

    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
    var rootUrl = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    if (rootUrl.slice(-1) !== '/') rootUrl += '/';

    var genderButtons = root.querySelectorAll('[data-mm-gender]');
    var steps = [
      { index: 0, el: root.querySelector('[data-mm-step="1"]') },
      { index: 1, el: root.querySelector('[data-mm-step="2"]') },
      { index: 2, el: root.querySelector('[data-mm-step="3"]') }
    ];
    var combo = root.querySelector('[data-mm-combo]');
    var sticky = root.querySelector('[data-mm-sticky]');
    var errorEl = root.querySelector('[data-mm-error]');

    var state = { gender: root.getAttribute('data-default-gender') || 'men', picked: [null, null, null] };
    var railCache = {};
    var railLimit = parseInt(root.getAttribute('data-rail-limit'), 10) || 12;

    // The rail view returns up to twelve; the theme editor decides how many of
    // those actually show, so a merchandiser can tighten the section without a
    // second request shape.
    function trimRail(rail) {
      var cards = rail.querySelectorAll('[data-mm-card]');
      for (var i = railLimit; i < cards.length; i++) cards[i].remove();
    }

    /* ---- rails ---- */

    function railFor(step) { return step.el ? step.el.querySelector('[data-mm-rail]') : null; }

    // Filters live on the step as two attributes written by
    // assets/mp-mm-filters.js: a tag path (/brand-nike+color-black) and a
    // query tail (&sort_by=...&filter.v.price.lte=...). Both are Shopify's own
    // filtering, applied server-side before this rail is rendered.
    function railUrl(step, handle) {
      var path = (step.el && step.el.getAttribute('data-mm-filter-path')) || '';
      var params = (step.el && step.el.getAttribute('data-mm-filter-params')) || '&sort_by=best-selling';
      return rootUrl + 'collections/' + handle + path + '?view=mm-rail' + params;
    }

    // The rail answers with cards AND a description of what that collection
    // can be filtered by. The description is handed on and removed before the
    // cards are bound, so it never lands in the DOM.
    function absorbMeta(rail, step, handle) {
      var node = rail.querySelector('[data-mm-rail-meta]');
      var meta = null;
      if (node) {
        try { meta = JSON.parse(node.textContent); } catch (e) { meta = null; }
        node.parentNode.removeChild(node);
      }
      if (step.el) step.el.dispatchEvent(new CustomEvent('mm:rail', { detail: { meta: meta, handle: handle } }));
    }

    function loadRail(step, handle, label) {
      var rail = railFor(step);
      if (!rail) return;

      // A different category means different brands and sizes, so a tag path
      // built for the old one would filter against tags this collection has
      // never heard of — and Shopify answers an unknown tag by returning
      // everything, which would look like the filter simply did nothing.
      if (step.handle && step.handle !== handle && step.el) {
        step.el.removeAttribute('data-mm-filter-path');
      }
      step.handle = handle;
      step.label = label;

      var url = railUrl(step, handle);
      if (railCache[url]) {
        rail.innerHTML = railCache[url];
        absorbMeta(rail, step, handle);
        trimRail(rail);
        bindCards(rail, step);
        return;
      }
      rail.classList.add('is-loading');
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          railCache[url] = html;
          rail.innerHTML = html;
          absorbMeta(rail, step, handle);
          trimRail(rail);
          bindCards(rail, step);
        })
        .catch(function () {
          rail.innerHTML = '<p class="mm-empty">Could not load this category. ' +
            '<a href="' + rootUrl + 'collections/' + handle + '">Browse ' + (label || 'it') + '</a>.</p>';
        })
        .finally(function () { rail.classList.remove('is-loading'); });
    }

    steps.forEach(function (step) {
      if (!step.el) return;
      step.el.addEventListener('mm:reload', function () {
        if (step.handle) loadRail(step, step.handle, step.label);
      });
    });

    function bindCards(scope, step) {
      var cards = scope.querySelectorAll('[data-mm-card]');
      Array.prototype.forEach.call(cards, function (el) {
        if (el.dataset.mmBound === '1') return;
        el.dataset.mmBound = '1';
        var btn = el.querySelector('.mm-card__btn');
        if (btn) btn.addEventListener('click', function () { pick(step, el); });
        el.addEventListener('click', function (event) {
          var size = event.target.closest ? event.target.closest('.mm-size') : null;
          if (!size || !el.contains(size)) return;
          Array.prototype.forEach.call(el.querySelectorAll('.mm-size'), function (s) {
            s.setAttribute('aria-pressed', s === size ? 'true' : 'false');
          });
          render();
        });
      });
    }

    /* ---- categories ---- */

    function categoriesFor(stepIndex) {
      var all = Array.prototype.slice.call(root.querySelectorAll('[data-mm-cat][data-step="' + (stepIndex + 1) + '"]'));
      return all.filter(function (chip) {
        var g = chip.getAttribute('data-gender');
        if (g !== 'both' && g !== state.gender) return false;
        if (stepIndex >= 1) {
          var prev = state.picked[stepIndex - 1];
          if (!prev) return false;
          // Free pairing means exactly that: everything in this gender stays on
          // the table no matter what came before it.
          if (freePairing) return true;
          // Otherwise every step after the first only offers what the pick
          // before it actually goes with. The pairing list is authored per
          // category in the theme editor, so a merchandiser can change what
          // "goes with" means without a deploy. Step three reuses the same
          // lists, read off the second pick.
          var pairs = ((prev.chip && prev.chip.getAttribute('data-pairs')) || '').toLowerCase();
          if (!pairs) return true;
          var key = (chip.getAttribute('data-key') || '').toLowerCase();
          return pairs.split(',').map(function (s) { return s.trim(); }).indexOf(key) > -1;
        }
        return true;
      });
    }

    function paintCategories(stepIndex) {
      var step = steps[stepIndex];
      if (!step.el) return;
      var visible = categoriesFor(stepIndex);
      Array.prototype.forEach.call(root.querySelectorAll('[data-mm-cat][data-step="' + (stepIndex + 1) + '"]'), function (chip) {
        chip.hidden = visible.indexOf(chip) === -1;
        if (chip.hidden) chip.setAttribute('aria-pressed', 'false');
      });
      var active = visible.filter(function (c) { return c.getAttribute('aria-pressed') === 'true'; })[0];
      if (!active && visible.length) {
        active = visible[0];
        // Opening step three on the same category step two is showing is how a
        // builder ends up recommending four more identical caps. Start it
        // somewhere else whenever there is somewhere else to start.
        if (stepIndex > 0) {
          var prevChip = root.querySelector('[data-mm-cat][data-step="' + stepIndex + '"][aria-pressed="true"]');
          var prevKey = prevChip ? prevChip.getAttribute('data-key') : '';
          for (var vi = 0; vi < visible.length; vi++) {
            if (visible[vi].getAttribute('data-key') !== prevKey) { active = visible[vi]; break; }
          }
        }
        active.setAttribute('aria-pressed', 'true');
      }
      if (active) loadRail(step, active.getAttribute('data-collection'), active.getAttribute('data-key'));
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-mm-cat]'), function (chip) {
      chip.addEventListener('click', function () {
        var stepIndex = parseInt(chip.getAttribute('data-step'), 10) - 1;
        Array.prototype.forEach.call(root.querySelectorAll('[data-mm-cat][data-step="' + (stepIndex + 1) + '"]'), function (c) {
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        loadRail(steps[stepIndex], chip.getAttribute('data-collection'), chip.getAttribute('data-key'));
        track('offer_category_click', { category: chip.getAttribute('data-key'), step: stepIndex + 1, gender: state.gender });
      });
    });

    /* ---- picking ---- */

    function pick(step, el) {
      var i = step.index;
      var already = state.picked[i] && state.picked[i].el === el;

      if (state.picked[i] && state.picked[i].el !== el) {
        state.picked[i].el.classList.remove('is-selected');
        var oldBtn = state.picked[i].el.querySelector('.mm-card__btn');
        if (oldBtn) oldBtn.setAttribute('aria-pressed', 'false');
      }

      if (already) { clearSlot(i); return; }

      var chipEl = root.querySelector('[data-mm-cat][data-step="' + (i + 1) + '"][aria-pressed="true"]');
      state.picked[i] = { el: el, chip: chipEl, card: readCard(el) };
      el.classList.add('is-selected');
      var btn = el.querySelector('.mm-card__btn');
      if (btn) btn.setAttribute('aria-pressed', 'true');
      renderSizes(el);

      var addEvents = ['offer_first_item_add', 'offer_second_item_add', 'offer_third_item_add'];
      track(addEvents[i] || 'offer_item_add', {
        product_id: state.picked[i].card.id,
        product_title: state.picked[i].card.title,
        category: chipEl ? chipEl.getAttribute('data-key') : '',
        gender: state.gender
      });
      track('offer_product_click', { product_id: state.picked[i].card.id, step: i + 1 });

      if (i < 2) paintCategories(i + 1);
      render();
    }

    function clearSlot(i) {
      if (state.picked[i]) {
        state.picked[i].el.classList.remove('is-selected');
        var b = state.picked[i].el.querySelector('.mm-card__btn');
        if (b) b.setAttribute('aria-pressed', 'false');
      }
      state.picked[i] = null;
      // Removing a piece invalidates every pick that was chosen to match it —
      // unless pairing is free, in which case later picks were never chosen to
      // match anything and there is no reason to throw them away.
      if (i < 2) {
        if (!freePairing) clearSlot(i + 1);
        paintCategories(i + 1);
      }
      render();
    }

    /* ---- render ---- */

    var firedRate = 0;
    var slotLabels = ['First piece', 'Second piece', 'Third piece'];

    // The tier ladder is the whole offer stated once, in public, before anyone
    // has picked anything: 2 pieces buys one rate, 3 buys a better one. It
    // then tracks the real count, so it doubles as the progress meter.
    function paintTiers(count, rate) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-mm-tier]'), function (row) {
        var need = parseInt(row.getAttribute('data-mm-tier'), 10) || 0;
        var reached = count >= need;
        var thisRate = need >= 3 ? tier3 : tier2;
        var current = reached && rate === thisRate;

        row.classList.toggle('is-reached', reached);
        row.classList.toggle('is-current', current);

        var status = row.querySelector('[data-mm-tier-status]');
        if (!status) return;
        if (current) {
          status.textContent = 'Applied';
        } else if (reached) {
          status.textContent = '';
        } else {
          var missing = need - count;
          status.textContent = 'Add ' + missing + ' more';
        }
      });
    }

    function render() {
      var chosen = state.picked.filter(Boolean);
      var count = chosen.length;

      if (steps[1].el) steps[1].el.hidden = !state.picked[0];
      // The third step only earns its place if there is actually something to
      // put in it. With free pairing there always is, so it opens as soon as
      // two pieces are down.
      if (steps[2].el) {
        var thirdHasStock = freePairing || !!root.querySelector('[data-mm-cat][data-step="3"]:not([hidden])');
        steps[2].el.hidden = !(state.picked[0] && state.picked[1] && thirdHasStock);
      }
      if (combo) combo.hidden = count === 0;

      for (var i = 0; i < 3; i++) {
        var slot = root.querySelector('[data-mm-slot="' + i + '"]');
        if (!slot) continue;
        var p = state.picked[i];
        if (!p) {
          slot.className = 'mm__slot mm__slot--empty';
          slot.innerHTML =
            '<span class="mm__slot-media" aria-hidden="true"></span>' +
            '<span class="mm__slot-body"><span class="mm__slot-label">' +
            slotLabels[i] +
            '</span><span class="mm__slot-title">' +
            (i === 2 ? 'Optional — takes you to ' + tier3 + '%' : 'Not chosen yet') +
            '</span></span>';
          continue;
        }
        var v = chosenVariant(p.el);
        var needsSize = p.card.variants.length > 1 && !v;
        slot.className = 'mm__slot';
        slot.innerHTML =
          '<span class="mm__slot-media">' + (p.card.image ? '<img src="' + p.card.image + '" alt="" width="56" height="70" loading="lazy">' : '') + '</span>' +
          '<span class="mm__slot-body">' +
          (p.card.brand ? '<span class="mm__slot-label">' + p.card.brand + '</span>' : '') +
          '<span class="mm__slot-title">' + p.card.title + '</span>' +
          '<span class="mm__slot-price">' + formatMoney(p.card.price, moneyFormat) + '</span>' +
          '<span class="mm__slot-size">' + (needsSize ? 'Choose a size on the card' : (v && p.card.variants.length > 1 ? 'Size ' + v.t : '')) + '</span>' +
          '<button type="button" class="mm__slot-remove" data-mm-remove="' + i + '">Remove</button>' +
          '</span>';
      }

      var subtotal = 0;
      var ready = true;
      chosen.forEach(function (p) {
        subtotal += p.card.price;
        if (p.card.variants.length > 1 && !chosenVariant(p.el)) ready = false;
      });

      // Two picks is the qualifying quantity, so the rate shown is the tier the
      // cart will actually reach — never a tier above it.
      var rate = count >= 3 ? tier3 : (count >= 2 ? tier2 : 0);
      var save = Math.round(subtotal * rate / 100);
      var now = subtotal - save;

      paintTiers(count, rate);

      setText('[data-mm-was]', formatMoney(subtotal, moneyFormat));
      setText('[data-mm-now]', formatMoney(now, moneyFormat));
      setText('[data-mm-save]', formatMoney(save, moneyFormat));
      setText('[data-mm-rate]', rate ? rate + '%' : '');

      var wasRow = root.querySelector('[data-mm-was-row]');
      var saveRow = root.querySelector('[data-mm-save-row]');
      if (wasRow) wasRow.hidden = rate === 0;
      if (saveRow) saveRow.hidden = rate === 0;

      var hint = root.querySelector('[data-mm-hint]');
      if (hint) {
        if (count === 0) hint.textContent = 'Pick your first piece to start.';
        else if (count === 1) hint.textContent = 'Add one more piece and ' + tier2 + '% comes off both, automatically.';
        else if (!ready) hint.textContent = 'Choose a size on each piece to continue.';
        else if (count === 2) hint.textContent = tier2 + '% off unlocked. Add a third piece and it becomes ' + tier3 + '%.';
        else hint.textContent = tier3 + '% off unlocked — that is the best tier.';
      }

      var cta = root.querySelector('[data-mm-add]');
      if (cta) {
        cta.disabled = !(count >= 2 && ready);
        var label = cta.querySelector('.btn-text') || cta;
        label.textContent = count >= 3
          ? 'Add all ' + count + ' to cart · ' + formatMoney(now, moneyFormat)
          : (count === 2 ? 'Add both to cart · ' + formatMoney(now, moneyFormat) : 'Pick 2 pieces');
      }

      if (sticky) {
        sticky.hidden = false;
        sticky.classList.toggle('is-visible', count > 0);
        setText('[data-mm-sticky-lead]', count >= 2 ? rate + '% unlocked' : count + ' item selected');
        setText('[data-mm-sticky-main]', count >= 2
          ? (ready
              ? formatMoney(now, moneyFormat) + (count === 2 ? ' · add a third for ' + tier3 + '%' : '')
              : 'Choose a size to continue')
          : 'Pick one more to unlock ' + tier2 + '% off');
        var scta = sticky.querySelector('[data-mm-sticky-cta]');
        if (scta) {
          scta.disabled = !(count >= 2 && ready);
          scta.textContent = count >= 3 ? 'Add ' + count : (count === 2 ? 'Add both' : 'Pick 2');
        }
      }

      // Reaching the better tier is a second unlock, not a repeat of the
      // first, so it is worth its own event. Dropping back does not fire.
      if (rate > firedRate) {
        firedRate = rate;
        track('offer_unlocked', { rate: rate, items: count, value: now / 100, gender: state.gender });
      }
      if (rate === 0) firedRate = 0;
    }

    function setText(sel, value) {
      Array.prototype.forEach.call(root.querySelectorAll(sel), function (el) { el.textContent = value; });
    }

    root.addEventListener('click', function (event) {
      var rm = event.target.closest ? event.target.closest('[data-mm-remove]') : null;
      if (rm && root.contains(rm)) clearSlot(parseInt(rm.getAttribute('data-mm-remove'), 10));
    });

    /* ---- gender ---- */

    Array.prototype.forEach.call(genderButtons, function (btn) {
      btn.addEventListener('click', function () {
        var g = btn.getAttribute('data-mm-gender');
        if (g === state.gender) return;
        state.gender = g;
        Array.prototype.forEach.call(genderButtons, function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        clearSlot(0);
        paintCategories(0);
        paintLooks();
        var viewAll = root.querySelector('[data-mm-viewall]');
        if (viewAll) {
          var href = btn.getAttribute('data-collection-url');
          if (href) viewAll.setAttribute('href', href);
        }
        track('offer_category_click', { category: 'gender:' + g, step: 0, gender: g });
      });
    });

    /* ---- add to cart ---- */

    function addItems(items, source) {
      if (errorEl) errorEl.hidden = true;
      var buttons = root.querySelectorAll('[data-mm-add], [data-mm-sticky-cta], .mm__look-cta');
      Array.prototype.forEach.call(buttons, function (b) { b.setAttribute('aria-busy', 'true'); });

      return fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok) {
            var msg = (res.data && (res.data.description || res.data.message)) || 'That size just sold out. Try another.';
            if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
            return;
          }
          track('offer_bundle_add', {
            source: source,
            items: items.length,
            gender: state.gender,
            product_ids: items.map(function (i) { return i.id; })
          });
          // The theme's cart drawer listens for this on document and re-fetches
          // its own section, so the drawer opens showing Shopify's real totals
          // rather than anything computed here.
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        })
        .catch(function () {
          if (errorEl) { errorEl.textContent = 'Something went wrong adding these. Please try again.'; errorEl.hidden = false; }
        })
        .finally(function () {
          Array.prototype.forEach.call(buttons, function (b) { b.removeAttribute('aria-busy'); });
        });
    }

    function addCombo() {
      var items = [];
      var incomplete = false;
      state.picked.filter(Boolean).forEach(function (p) {
        var v = chosenVariant(p.el);
        if (!v) { incomplete = true; renderSizes(p.el); return; }
        items.push({ id: v.id, quantity: 1 });
      });
      if (incomplete || items.length < 2) { render(); return; }
      addItems(items, 'builder');
    }

    var addBtn = root.querySelector('[data-mm-add]');
    if (addBtn) addBtn.addEventListener('click', addCombo);
    var stickyCta = root.querySelector('[data-mm-sticky-cta]');
    if (stickyCta) stickyCta.addEventListener('click', addCombo);

    /* ---- curated looks ---- */

    function paintLooks() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-mm-look]'), function (look) {
        var g = look.getAttribute('data-gender');
        look.hidden = !(g === 'both' || g === state.gender);
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-mm-look]'), function (look) {
      var cards = look.querySelectorAll('[data-mm-card]');
      // Every piece starts in the look, and any piece can be taken out. Nobody
      // is forced to buy the whole thing.
      Array.prototype.forEach.call(cards, function (el) {
        el.classList.add('is-selected');
        var btn = el.querySelector('.mm-card__btn');
        if (btn) {
          btn.setAttribute('aria-pressed', 'true');
          btn.addEventListener('click', function () {
            el.classList.toggle('is-selected');
            btn.setAttribute('aria-pressed', el.classList.contains('is-selected') ? 'true' : 'false');
            if (el.classList.contains('is-selected')) renderSizes(el);
            paintLookTotal(look);
          });
        }
        el.addEventListener('click', function (event) {
          var size = event.target.closest ? event.target.closest('.mm-size') : null;
          if (!size || !el.contains(size)) return;
          Array.prototype.forEach.call(el.querySelectorAll('.mm-size'), function (s) {
            s.setAttribute('aria-pressed', s === size ? 'true' : 'false');
          });
          paintLookTotal(look);
        });
        renderSizes(el);
      });

      var cta = look.querySelector('.mm__look-cta');
      if (cta) {
        cta.addEventListener('click', function () {
          var items = [];
          var incomplete = false;
          Array.prototype.forEach.call(look.querySelectorAll('[data-mm-card].is-selected'), function (el) {
            var v = chosenVariant(el);
            if (!v) { incomplete = true; renderSizes(el); return; }
            items.push({ id: v.id, quantity: 1 });
          });
          if (incomplete || items.length === 0) { paintLookTotal(look); return; }
          addItems(items, 'look');
        });
      }

      paintLookTotal(look);
    });

    function paintLookTotal(look) {
      var selected = look.querySelectorAll('[data-mm-card].is-selected');
      var subtotal = 0;
      var ready = true;
      Array.prototype.forEach.call(selected, function (el) {
        var c = readCard(el);
        subtotal += c.price;
        if (c.variants.length > 1 && !chosenVariant(el)) ready = false;
      });
      var n = selected.length;
      var rate = n >= 3 ? tier3 : (n >= 2 ? tier2 : 0);
      var now = subtotal - Math.round(subtotal * rate / 100);

      var wasEl = look.querySelector('[data-mm-look-was]');
      var nowEl = look.querySelector('[data-mm-look-now]');
      if (wasEl) { wasEl.textContent = rate ? formatMoney(subtotal, moneyFormat) : ''; wasEl.hidden = !rate; }
      if (nowEl) nowEl.textContent = formatMoney(now, moneyFormat);

      var cta = look.querySelector('.mm__look-cta');
      if (cta) {
        cta.disabled = n === 0;
        cta.textContent = n === 0 ? 'Nothing selected'
          : (!ready ? 'Choose a size' : (n > 1 ? 'Add ' + n + ' pieces · ' + formatMoney(now, moneyFormat) : 'Add 1 piece'));
      }
    }

    /* ---- boot ---- */

    Array.prototype.forEach.call(root.querySelectorAll('[data-mm-rail]'), function (rail) {
      var host = rail.closest('[data-mm-step]');
      var n = host ? parseInt(host.getAttribute('data-mm-step'), 10) : 1;
      bindCards(rail, steps[(n || 1) - 1] || steps[0]);
    });
    paintCategories(0);
    paintLooks();
    render();

    if ('IntersectionObserver' in window) {
      var seen = false;
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || seen) return;
          seen = true;
          track('offer_section_view', { section: 'mix-match', gender: state.gender });
          obs.disconnect();
        });
      }, { threshold: 0.25 }).observe(root);
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mm-root]'), initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: a section re-render replaces the markup, so re-init it.
  document.addEventListener('shopify:section:load', boot);
})();
